import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static values = { url: String };

  connect() {
    this.lastSavedEmail = this.normalizedEmail();
    this.debounceTimer = null;
    this.requestSeq = 0;
    this.abortController = null;
    this.form = this.element.closest('form');

    this.boundPersist = () => this.schedulePersist();
    this.boundAbandon = () => this.abandonPendingPersist();

    this.element.addEventListener('blur', this.boundPersist);
    // Cancel a pending blur PATCH so it cannot land after submit and overwrite
    // the email validate_checkout / pay just applied from this field.
    this.form?.addEventListener('submit', this.boundAbandon);
  }

  disconnect() {
    this.element.removeEventListener('blur', this.boundPersist);
    this.form?.removeEventListener('submit', this.boundAbandon);
    this.abandonPendingPersist();
  }

  schedulePersist() {
    clearTimeout(this.debounceTimer);
    this.cancelInFlight();
    this.debounceTimer = setTimeout(() => this.persist(), 400);
  }

  abandonPendingPersist() {
    clearTimeout(this.debounceTimer);
    this.cancelInFlight();
  }

  // Abort the previous PATCH so it cannot land after a newer blur (or a revert).
  cancelInFlight() {
    this.requestSeq += 1;
    this.abortController?.abort();
  }

  async persist() {
    if (!this.hasUrlValue) return;

    const email = this.element.value.trim();
    this.cancelInFlight();
    if (email.toLowerCase() === this.lastSavedEmail) return;

    this.clearError();
    const requestId = ++this.requestSeq;
    this.abortController = new AbortController();
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const body = new FormData();
    body.append('email', email);

    let response;
    try {
      response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': token,
          Accept: 'application/json',
        },
        body,
        credentials: 'same-origin',
        signal: this.abortController.signal,
      });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      return;
    }

    if (requestId !== this.requestSeq) return;
    if (this.normalizedEmail() !== email.toLowerCase()) return;

    if (response.ok) {
      const data = await response.json().catch(() => ({}));
      if (data.email) {
        this.element.value = data.email;
      }
      this.lastSavedEmail = this.normalizedEmail();
      return;
    }

    if (response.status === 422) {
      const data = await response.json().catch(() => ({}));
      const message = data.errors?.[0] || 'Please enter a valid email.';
      this.showError(message);
    }
  }

  normalizedEmail() {
    return this.element.value.trim().toLowerCase();
  }

  showError(message) {
    this.clearError();
    const parent = this.element.parentElement;
    if (!parent) return;
    parent.classList.add('error');
    const hint = document.createElement('span');
    hint.className = 'help-block error';
    hint.textContent = message;
    parent.appendChild(hint);
  }

  clearError() {
    const parent = this.element.parentElement;
    if (!parent?.classList.contains('error')) return;
    parent.classList.remove('error');
    parent.querySelectorAll(':scope > .help-block.error').forEach((el) => {
      el.remove();
    });
  }
}
